import React, { useEffect, useState } from 'react'

import {
  BorderStyle,
  Box,
  Color,
  Display,
  FontFamily,
  FontWeight,
  Heading2,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { RibbonTriggerContainer } from 'RibbonTrigger'

import Overlay from 'components/Overlay'
import { AgeVerificationPopUp } from 'components/Product/AgeVerification'
import { MultipleBoxesDeliveryMessage } from 'routes/OrderConfirmation/components/MultipleBoxesDeliveryMessage/MultipleBoxesDeliveryMessage'
import { PageLoader } from 'routes/OrderConfirmation/components/PageLoader'

import {
  DISCOUNT_BANNER_BREAKPOINT_PX,
  DiscountSavingsBanner,
} from './components/DiscountSavingsBanner'
import { Market } from './components/Market'
import { OrderConfirmationHeader } from './components/OrderConfirmationHeader'
import { OrderFeedback } from './components/OrderFeedback/OrderFeedback'
import { ReferAFriend } from './components/ReferAFriend'
import { SectionStage } from './components/SectionStage/SectionStage'
import { ResubscriptionPrompt, useShowDiscountSavingsBanner } from './experiments/GoustoAnytime'

type HeaderDetails = {
  deliveryDate: string
  deliveryStart: string
  deliveryEnd: string
  whenCutoffTime: string
  whenCutoffDate: string
}

type OrderConfirmationProps = {
  ageVerified: boolean
  canEditOrder: boolean
  headerDetails?: HeaderDetails
  orderId: string
  showHeader: boolean
  showMultipleBoxesDeliveryMessage: boolean
  userFetchReferralOffer: () => void
  userVerifyAge: (isOver18: boolean, rememberChoice: boolean) => void
  userLoadData: () => void
  isLoading: boolean
  isTransactional: boolean
}

const PageContent = withCitrusTheme(Box, () => ({
  maxWidth: '73rem',
  margin: '0 auto',
}))

const SectionStack = withCitrusTheme(Box, (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [`@media (max-width: ${theme.tokens.breakpoints[0]})`]: {
    gap: '14px',
  },
}))

const SectionCard = withCitrusTheme(Box, () => ({
  background: 'white',
  border: '1px solid #ced4d9',
  borderRadius: '8px',
  overflow: 'hidden',
}))

const MarketSectionInner = withCitrusTheme(Box, (theme) => ({
  padding: '1.25rem',
  [`@media (max-width: ${theme.tokens.breakpoints[0]})`]: {
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
  },
}))

const MarketPlaceTitle = withCitrusTheme(Heading2, () => ({
  textAlign: 'center',
  fontFamily: 'Axiforma',
  fontWeight: 700,
  fontSize: '24px',
  WebkitFontSmoothing: 'antialiased',
  marginBottom: '20px',
  paddingTop: '20px',
}))

const MobileOnly = withCitrusTheme(Box, (theme) => ({
  [`@media (min-width: ${theme.tokens.breakpoints[0]})`]: {
    display: 'none !important',
  },
}))

// Only renders as a full-width bar here
const VisibleAtOrBelowDiscountBannerBreakpoint = withCitrusTheme(Box, () => ({
  [`@media (min-width: ${DISCOUNT_BANNER_BREAKPOINT_PX + 1}px)`]: {
    display: 'none',
  },
}))

const RafContainer = withCitrusTheme(Box, () => ({
  background: 'white',
  padding: '12px',
  borderRadius: '6px',
  border: '1px solid #ced4d9',
}))

export const OrderConfirmation = ({
  ageVerified,
  canEditOrder,
  userFetchReferralOffer,
  userVerifyAge,
  userLoadData,
  headerDetails,
  isLoading,
  showHeader,
  showMultipleBoxesDeliveryMessage,
  orderId,
  isTransactional,
}: OrderConfirmationProps) => {
  const [hasConfirmedAge, setHasConfirmedAge] = useState(false)
  const [showAgeVerification, setShowAgeVerification] = useState(false)

  useEffect(() => {
    userFetchReferralOffer()
  }, [userFetchReferralOffer])

  useEffect(() => {
    userLoadData()
  }, [userLoadData])

  const toggleAgeVerificationPopUp = () => {
    setShowAgeVerification(!showAgeVerification)
  }

  const onAgeConfirmation = (isOver18: boolean) => {
    setHasConfirmedAge(true)
    userVerifyAge(isOver18, true)
  }

  const isUnderAge = hasConfirmedAge && !ageVerified

  const {
    shouldShow: showDiscountSavingsBanner,
    percentage: discountPercentage,
    amountSaved: discountAmountSaved,
  } = useShowDiscountSavingsBanner(isTransactional, showHeader)

  if (isLoading) {
    return <PageLoader />
  }

  const orderConfirmationSection = (
    <SectionStage
      completed
      step={1}
      title={
        isTransactional ? (
          <>
            Good food is on the way!{' '}
            <Text
              display={Display.Inline}
              fontFamily={FontFamily.Book}
              fontWeight={FontWeight.Normal}
              size={[4, null, 6]}
            >
              Everything&apos;s sorted for your one-off box
            </Text>
          </>
        ) : (
          'Order created'
        )
      }
      showLineBelow
    >
      {showDiscountSavingsBanner && (
        <VisibleAtOrBelowDiscountBannerBreakpoint style={{ marginBottom: '1rem' }}>
          <DiscountSavingsBanner
            variant="bar"
            percentage={discountPercentage}
            amountSaved={discountAmountSaved}
          />
        </VisibleAtOrBelowDiscountBannerBreakpoint>
      )}
      <SectionCard
        bg={Color.NeutralGrey_50}
        paddingH={5}
        style={{ paddingTop: '1.25rem', paddingBottom: '1rem' }}
      >
        {headerDetails && (
          <OrderConfirmationHeader
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...headerDetails}
            isTransactional={isTransactional}
            canEditOrder={canEditOrder}
            showDiscountSavingsBanner={showDiscountSavingsBanner}
            discountPercentage={discountPercentage}
            discountAmountSaved={discountAmountSaved}
          />
        )}
        {showMultipleBoxesDeliveryMessage && (
          <MultipleBoxesDeliveryMessage data-testid="multipleDeliveriesAlert" />
        )}
        <MobileOnly style={{ marginTop: '1rem' }}>
          <RafContainer>
            <ReferAFriend />
          </RafContainer>
        </MobileOnly>
        <MobileOnly style={{ marginTop: '1rem' }}>
          <OrderFeedback orderId={orderId} />
        </MobileOnly>
        <ResubscriptionPrompt />
      </SectionCard>
    </SectionStage>
  )

  const marketSection = (
    <SectionStage
      step={2}
      title="Add desserts, drinks, snacks & more from the Gousto Market"
      showLineAbove
    >
      <SectionCard
        bg={Color.White}
        borderColor={Color.NeutralGrey_200}
        borderRadius={1}
        borderStyle={BorderStyle.Solid}
        borderWidth={1}
        style={{
          overflow: 'hidden',
        }}
      >
        {showHeader ? (
          <MarketSectionInner style={{ paddingTop: '1.25rem', paddingBottom: '1.25rem' }}>
            <Market
              ageVerified={ageVerified}
              canEditOrder={canEditOrder}
              showContextualSides={canEditOrder}
              toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
            />
          </MarketSectionInner>
        ) : (
          <Box>
            <Market
              ageVerified={ageVerified}
              canEditOrder={canEditOrder}
              showContextualSides={canEditOrder}
              toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
            />
          </Box>
        )}
      </SectionCard>
    </SectionStage>
  )

  return (
    <Box
      paddingH={3}
      style={{
        paddingTop: '1rem',
        paddingBottom: '2rem',
      }}
    >
      <PageContent data-testing="orderConfirmationContainer">
        <Overlay open={showAgeVerification} from="top">
          <AgeVerificationPopUp
            onClose={toggleAgeVerificationPopUp}
            isUnderAge={isUnderAge}
            onAgeConfirmation={onAgeConfirmation}
          />
        </Overlay>

        {showHeader ? (
          <SectionStack>
            {orderConfirmationSection}
            {marketSection}
          </SectionStack>
        ) : (
          <Box>
            <MarketPlaceTitle>Gousto Market</MarketPlaceTitle>
            <SectionCard
              bg={Color.White}
              borderColor={Color.NeutralGrey_200}
              borderRadius={1}
              borderStyle={BorderStyle.Solid}
              borderWidth={1}
            >
              <MarketSectionInner style={{ paddingTop: '1.25rem', paddingBottom: '1.25rem' }}>
                <Market
                  ageVerified={ageVerified}
                  canEditOrder={canEditOrder}
                  showContextualSides={false}
                  toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
                />
              </MarketSectionInner>
            </SectionCard>
            <MobileOnly paddingH={4} style={{ marginTop: '1rem' }}>
              <ReferAFriend />
            </MobileOnly>
          </Box>
        )}
      </PageContent>
      <RibbonTriggerContainer name="order-confirmation-web" />
    </Box>
  )
}
